import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Paper } from '@mui/material'
import { GridCell } from '@mui/x-data-grid'
import { POTENTIAL_TABLE_COLUMNS } from '../../constants'
import { marketerPotentialTableStyles } from './marketer-potential-table.styles'
import { TableUrlPagination } from '../../../../ui'
import { setSelectedPotentials } from '../../slices'
import { marketerPotentialsDataPreparation } from '../../logics/marketer-potentials.logics'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const MarketerPotentialTable = () => {
  const { formatMessage } = useIntl()
  const intl = useIntl()
  const dispatch = useDispatch()
  const {
    data,
    search,
    pagination: { total_potenciales: totalEntries },
    isLoading,
    selectedRows,
  } = useSelector((state) => state.marketerPotential)
  const combos = useSelector((state) => state.combos)

  const handleSelectRows = useCallback((rows) => dispatch(setSelectedPotentials(rows)), [dispatch])

  const formattedColumns = useMemo(
    () =>
      [...POTENTIAL_TABLE_COLUMNS].map((column) => {
        // Base column configuration for Material React Table
        const baseColumn = {
          accessorKey: column.field,
          id: column.field,
          size: column.width || 200,
          minSize: column.minWidth || 100,
          maxSize: column.maxWidth,
          enableSorting: column.sorting !== false,
          muiTableHeadCellProps: {
            align: column.align || 'left',
            sx: {
              ...marketerPotentialTableStyles.header,
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              whiteSpace: 'normal',
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
          muiTableBodyCellProps: {
            align: column.align || 'left',
            sx: {
              ...marketerPotentialTableStyles.cell,
              minWidth: column.minWidth || 100,
            },
          },
        }

        // Handle special columns
        if (column.field === 'active') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return value || <span>--</span>
            },
          }
        }

        // Handle regular columns
        const result = {
          ...baseColumn,
          header: formatMessage({
            id: `pages.marketer.potential.table.columns.${column.field}.title`,
          }),
        }

        // Add lookup functionality if combo data exists
        if (
          column.hasOwnProperty('comboId') &&
          combos[column.comboId] &&
          combos[column.comboId].data
        ) {
          const lookup = getLookup(combos[column.comboId].data)
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            return lookup[value] || value || <span>0</span>
          }
        } else {
          // Default cell renderer with empty value handling
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            return value || <span>0</span>
          }
        }

        return result
      }),
    [combos, formatMessage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const processedData = useMemo(() => {
    const selectedIds = selectedRows.map((el) => el.id)
    return marketerPotentialsDataPreparation(POTENTIAL_TABLE_COLUMNS, data, { intl }).map((row) => {
      if (selectedIds.indexOf(row.id) !== -1) {
        row.tableData.checked = true
      }
      return row
    })
  }, [data, intl, selectedRows])

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      fixedColumns: {
        right: 0,
      },
      tableLayout: 'fixed',
      headerStyle: marketerPotentialTableStyles.header,
      draggable: false,
    }),
    []
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={isLoading}
      onSelectionChange={handleSelectRows}
    />
  )
}
